import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import CheckInFlow from './CheckInFlow';
import CheckInFlowWithVoice from './CheckInFlowWithVoice';
import VoiceIntakeDemo from './pages/admin/VoiceIntakeDemo';
import ThankYouPage from './components/ThankYouPage';
import Navigation from './components/Navigation';
import { clearTokens, decodePinJwt, getTokensFromStorage, listenForMiraSolAdminUIToken, isInIframe } from './utils/token';
import { useTheme } from './components/redux/hooks/useTheme';
import { AuthProvider, useAuth, useUserRole, useIsAnkrAdmin, useAuthContext } from './context/AuthContext';
import HealthcareSelector from './components/shared/HealthcareSelector';
import OrganizationSelector from './components/shared/OrganizationSelector';
import UserProfileDropdown from './components/shared/UserProfileDropdown';
import { useUserData } from './components/redux/hooks/useUserData';
import { useSelector, useDispatch } from 'react-redux';
import { NotificationCenter } from './components/NovuNotificationCtr';
import { updateEvent } from './components/redux/slices/vcxEventsSlice';
import SupportDeskPage from './components/SupportDeskPage';
// Admin pages
import LoginPage from './pages/admin/LoginPage';
const ZoomMeetingPage = lazy(() => import('./pages/admin/ZoomMeetingPage'));
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import FormsManagementPage from './pages/admin/FormsManagementPage';
import HealthSystemsPage from './pages/admin/HealthSystemsPage';
import UsersPage from './pages/admin/UsersPage';
import PermissionDeniedPage from './pages/admin/PermissionDeniedPage';
import InitiateVisitPage from './pages/admin/InitiateVisitPage';
import FormPreviewPage from './pages/admin/FormPreviewPage';
import CasesPage from './pages/admin/CasesPage';
import CaseAuditLogsPage from './pages/admin/CaseAuditLogsPage';
import PatientDetailsPage from './pages/admin/PatientDetailsPage';
import PatientManagementPage from './pages/admin/PatientManagementPage';
import NoteTemplatesPage from './pages/admin/NoteTemplatesPage';
import NoteTemplateManagementPage from './pages/admin/NoteTemplateManagementPage';
import NoteTemplateAssociationsPage from './pages/admin/NoteTemplateAssociationsPage';
import ProviderDetailsPage from './pages/admin/ProviderDetailsPage';
import ProviderManagementPage from './pages/admin/ProviderManagementPage'; // Import the new page
import UserProfilePage from './pages/admin/UserProfilePage';
import VCXEventsPage from './pages/admin/VCXEventsPage';
import VoiceAIAgentsPage from './pages/admin/VoiceAIAgentsPage';
import AnkrUsersPage from './pages/admin/users/AnkrUsersPage';
import HealthcareUsersPage from './pages/admin/users/HealthcareUsersPage';
import PatientsPage from './pages/admin/users/PatientsPage';
import RolesPage from './pages/admin/RolesPage';
import SalesLeadsPage from './pages/sales-module/SalesLeadsPage';
import SalesOpportunityPage from './pages/sales-module/SalesOpportunityPage';
import SalesUsersPage from './pages/sales-module/SalesUsersPage';
import ReferralManagementPage from './pages/referral-management/ReferralManagementPage';
import ReferralManagementSettings from './components/referral-management/ReferralManagementSettings';
import SalesRolesPage from './pages/sales-module/SalesRolesPage';
import OrganizationsPage from './pages/sales-module/OrganizationsPage';
import SalesTeamsPage from './pages/sales-module/SalesTeamsPage';
import JobListingsPage from './pages/sales-module/JobListingsPage';
import ROICalculator from './pages/sales-module/ROICalculator';
import RetellAnalyticsDashboard from './pages/sales-module/RetellAnalyticsDashboard';
import RetellCallLogs from './pages/sales-module/RetellCallLogs';
import VoiceAICredentialsManager from './components/sales/VoiceAICredentialsManager';
import SalesModuleSettings from './components/sales/SalesModuleSettings';
import MfaModulePage from './pages/admin/MfaModulePage';
import CoreServerCredentialsPage from './pages/admin/CoreServerCredentialsPage';
import NovuSubscriberManagerPage from './pages/admin/NovuSubscriberManagerPage';
import CallbackManagementPage from './pages/admin/CallbackManagementPage'; // Import the new page
import NoteGenerationSandbox from './pages/admin/NoteGenerationSandbox';
import AdminNotFoundPage from './pages/admin/AdminNotFoundPage';
import ScopeManagementPage from './pages/admin/ScopeManagementPage';

// Import ShortUrlManagementPage
import ShortUrlManagementPage from './pages/admin/ShortUrlManagementPage';

// Import Feedback Components
import FeedbackSidePanel from './components/shared/FeedbackSidePanel';
import FeedbackTriggerService from './services/FeedbackTriggerService';
import { usePostHog } from './components/analytics/PostHogProvider';
import PostHogProvider from './components/analytics/PostHogProvider';
import adminApi from './services/api';

// Import VCXFSSOnboarding
import VCXFSSOnboarding from './pages/onboarding-module/arpit-old-fss-tbd/VCXFSSOnboarding';

// Import Onboarding Layout and Pages
import OnboardingLayout from './components/layouts/OnboardingLayout';
import SignupPage from './pages/onboarding-module/SignupPage';
import Welcome from './pages/onboarding-module/WelcomePage/Welcome';
import Dashboard from './pages/onboarding-module/Dashboard/Dashboard';
import EHRConnect from './pages/onboarding-module/EHRConnect/EHRConnect';
import ProvidersUpload from './pages/onboarding-module/ProvidersUpload/ProvidersUpload';
import PhoneSetup from './pages/onboarding-module/PhoneSetup/PhoneSetup';
import PhoneTreeEditor from './pages/onboarding-module/PhoneTreeEditor/PhoneTreeEditor';
// Lazy load Payment component to prevent Stripe.js from loading on every page
const Payment = lazy(() => import('./pages/onboarding-module/Payment/Payment'));

// Phone number formatting utilities
export const formatPhoneNumber = (value) => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  
  // Limit to maximum 10 digits
  const limitedDigits = digits.slice(0, 10);
  
  // Format based on length
  if (limitedDigits.length <= 9) {
    // Format as 123-456-789 (9 digits)
    return limitedDigits.replace(/(\d{3})(\d{1,3})?(\d{1,3})?/, (match, p1, p2, p3) => {
      let result = p1;
      if (p2) result += '-' + p2;
      if (p3) result += '-' + p3;
      return result;
    });
  } else {
    // Format as (123) 456-7890 (10 digits)
    return limitedDigits.replace(/(\d{3})(\d{3})?(\d{4})?/, (match, p1, p2, p3) => {
      let result = '(' + p1 + ')';
      if (p2) result += ' ' + p2;
      if (p3) result += '-' + p3;
      return result;
    });
  }
};

// Handle phone number input change with formatting
export const handlePhoneNumberChange = (e, setter) => {
  const formattedValue = formatPhoneNumber(e.target.value);
  setter(formattedValue);
};

// AdminRoute component for protected routes
const AdminRoute = ({ children, requireAnkrAdmin = false }) => {
  const { user, loading } = useAuthContext();
  const location = useLocation();
  const isAuthenticated = !loading && user !== null;
  const isAnkrAdmin = user?.isAnkrAdmin || false;
  const isHealthcareUser = user?.isHealthcareUser || false;
  const isSalesUser = user?.isSalesUser || false;

  // Wait until authentication check is complete
  if (loading) {
    return null; // Or a loading spinner component
  }

  // Allow access for authenticated ankr admins, healthcare users, or sales users
  if (!isAuthenticated || (!isAnkrAdmin && !isHealthcareUser && !isSalesUser)) {
    // Save current path for post-login redirect
    const currentPath = location.pathname + location.search;
    if (currentPath !== '/admin/login') {
      localStorage.setItem('redirect_after_login', currentPath);
    }
    // Redirect to login page if not authenticated or not an authorized user type
    return <Navigate to="/admin/login" replace />;
  }

  // If this route requires Ankr admin privileges and user is not an Ankr admin
  if (requireAnkrAdmin && !isAnkrAdmin && !isHealthcareUser) {
    // Redirect to a permission denied page or dashboard
    return <Navigate to="/admin/permission-denied" replace />;
  }

  return children;
};

// Component to handle root redirect based on domain
const RootRedirect = () => {
  const isDashboard = window.location.hostname === process.env.REACT_APP_DASHBOARD_HOST;
  return <Navigate to={isDashboard ? "/admin/login" : "/check-in"} replace />;
};

function App() {
  return (
    <PostHogProvider>
      <AuthProvider>
        <Router>
          <Routes>
            {/* Onboarding routes - separate layout without sidebar/navbar */}
            <Route element={<OnboardingLayout />}>
              <Route path="/onboarding" element={<SignupPage />} />
              <Route path="/onboarding/signup" element={<SignupPage />} />
              <Route path="/onboarding/post-signup" element={<Welcome />} />
              <Route path="/onboarding/dashboard" element={<Dashboard />} />
              <Route path="/onboarding/ehr-connect" element={<EHRConnect />} />
              <Route path="/onboarding/providers-upload" element={<ProvidersUpload />} />
              <Route path="/onboarding/phone-setup" element={<PhoneSetup />} />
              <Route path="/onboarding/phone-tree-editor" element={<PhoneTreeEditor />} />
              <Route path="/onboarding/payment" element={
                <Suspense fallback={<div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>}>
                  <Payment />
                </Suspense>
              } />
            </Route>
          </Routes>

          <MainLayout />
        </Router>
      </AuthProvider>
    </PostHogProvider>
  );
}

/**
 * MainLayout component - the default layout with sidebar and navbar
 * This component handles the conditional rendering based on route
 */
function MainLayout() {
  const location = window.location.pathname;

  // Auth Context - hooks must be called unconditionally
  const { user, logout } = useAuthContext();

  // Get patient info from Redux instead of local state
  const { patientInfo, healthcareSystemInfo, updateTokenData, resetTokenData } = useUserData();

  // State for dropdown
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  // State for avatar
  const [avatar, setAvatar] = React.useState({
    initials: '',
    style: {}
  });

  const { toggleDarkMode, setSidebarSize } = useTheme();

  // PostHog for feedback tracking
  const { captureEvent } = usePostHog();

  // Redux dispatch and get current event (if on VCX page)
  const dispatch = useDispatch();
  const currentEvent = useSelector(state => state.events?.currentEvent || null);

  // Handle session feedback submission
  const handleSessionFeedbackSubmit = async (surveyData) => {
    try {
      // 1. Track with PostHog
      if (captureEvent) {
        captureEvent('feedback_submitted', {
          ...surveyData,
          has_event: !!currentEvent,
          event_id: currentEvent?.id || null
        });
      }

      // 2. Save to localStorage
      const feedbackHistory = JSON.parse(localStorage.getItem('vcx_feedback_history') || '[]');
      feedbackHistory.push({
        ...surveyData,
        submitted_at: new Date().toISOString(),
        event_id: currentEvent?.id || null,
        user_id: user?.djangoUserId || null
      });
      localStorage.setItem('vcx_feedback_history', JSON.stringify(feedbackHistory));

      // 3. Save to backend if event is available
      if (currentEvent?.id) {
        const feedbackUpdate = {
          event_metadata: {
            ...currentEvent.event_metadata,
            session_feedback: {
              ...(currentEvent.event_metadata?.session_feedback || {}),
              ...surveyData,
              submitted_at: new Date().toISOString(),
              submitted_by: user?.djangoUserId
            }
          }
        };
        await adminApi.updateVCXEvent(currentEvent.id, feedbackUpdate);

        // Create a custom activity for feedback submission
        await adminApi.createVCXEventActivity(currentEvent.id, {
          activity_type: 'FEEDBACK_SUBMITTED',
          action: 'Session Feedback Submitted',
          notes: `User submitted session feedback with overall satisfaction: ${surveyData.overall_satisfaction}/5`,
          activity_metadata: {
            feedback_summary: {
              overall_satisfaction: surveyData.overall_satisfaction,
              productivity: surveyData.productivity,
              ease_of_use: surveyData.ease_of_use,
              has_issues: surveyData.issues_encountered?.length > 0 || false
            }
          }
        });

        dispatch(updateEvent({ ...currentEvent, ...feedbackUpdate }));
      }

      // 4. Mark as submitted in FeedbackTriggerService
      FeedbackTriggerService.markFeedbackSubmitted(
        captureEvent ? { captureEvent } : null,
        { overall_satisfaction: surveyData.overall_satisfaction }
      );
    } catch (error) {
      console.error('Failed to submit session feedback:', error);
      throw error;
    }
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [dropdownOpen]);

  // Retrieve patient information from stored tokens on component mount
  React.useEffect(() => {
    const storedTokens = getTokensFromStorage();
    if (storedTokens && storedTokens.pin_jwt) {
      // Decode the stored PIN-JWT to get patient info
      decodePinJwt(storedTokens.pin_jwt).then(tokenData => {
        if (tokenData && !tokenData.error) {
          // Update Redux store with token data
          updateTokenData(tokenData);
        } else {
          // console.error('Failed to decode stored token:', tokenData?.message);
          // Optionally clear invalid tokens
          clearTokens();
          resetTokenData();
        }
      });
    }
  }, []);

  // Initialize embed token listener for iframe integration
  React.useEffect(() => {
    if (isInIframe()) {
      // console.log('Running in iframe, initializing embed token listener...');
      const allowedEmbedOrigins = process.env.REACT_APP_EMBED_ALLOWED_ORIGINS ? process.env.REACT_APP_EMBED_ALLOWED_ORIGINS.split(',') : [];
      const cleanup = listenForMiraSolAdminUIToken(allowedEmbedOrigins);
      // Cleanup on unmount
      return cleanup;
    }
  }, []);

  // Generate avatar after patient info is loaded
  React.useEffect(() => {
    // Function to generate avatar with initials and gradient background
    const generateInitialsAvatar = () => {
      const firstInitial = patientInfo?.firstName ? patientInfo.firstName.charAt(0).toUpperCase() : '';
      const lastInitial = patientInfo?.lastName ? patientInfo.lastName.charAt(0).toUpperCase() : '';
      const initials = firstInitial + lastInitial || '?';
      
      // Generate a consistent color based on the initials
      const hash = initials.charCodeAt(0) + (initials.length > 1 ? initials.charCodeAt(1) : 0);
      const hue = hash % 360;
      
      // Create a soft gradient background
      const gradient = `linear-gradient(135deg, hsl(${hue}, 70%, 80%), hsl(${(hue + 60) % 360}, 70%, 60%))`;
      
      // Style for the avatar container
      const avatarStyle = {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        background: gradient,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 'bold',
        fontSize: '16px',
        textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
      };
      
      setAvatar({ initials, style: avatarStyle });
    };
    
    // Generate avatar if patient info is available
    if (patientInfo?.firstName || patientInfo?.lastName) {
      generateInitialsAvatar();
    }
  }, [patientInfo]);

  // Don't render main layout for onboarding routes
  if (location.startsWith('/onboarding')) {
    return null;
  }

  // Handle logout - use centralized logout from AuthContext
  const handleLogout = async () => {
    await logout('/admin/login');
  };

  // Handle sidebar toggle
  const handleSidebarToggle = () => {
    document.body.classList.toggle('sidebar-enable');

    // Toggle sidebar size on larger screens
    if (window.innerWidth >= 992) {
      const currentSize = document.body.getAttribute('data-sidebar-size');
      const navbarHeader = document.querySelector('.navbar-header');

      if (!currentSize || currentSize === 'lg') {
        setSidebarSize('small');
        // Add 'close' class when sidebar is collapsed
        if (navbarHeader) navbarHeader.classList.add('close');
      } else {
        setSidebarSize('default');
        // Remove 'close' class when sidebar is expanded
        if (navbarHeader) navbarHeader.classList.remove('close');
      }
    }
  };
  
  // Handle dark mode toggle
  const handleDarkModeToggle = () => {
    const currentTheme = document.body.getAttribute('data-bs-theme');
    const isDark = currentTheme !== 'dark';
    toggleDarkMode(isDark);
  };

  // Handle setting changes for layout, position, color, etc.
  const handleSettingChange = (attribute, value) => {
    document.body.setAttribute(attribute, value);
  };

  return (
    <div id="layout-wrapper">
          {/* ========== Top Bar ========== */}
          <header id="page-topbar" style={{ zIndex: 1000 }}>
            <div className="navbar-header d-flex justify-content-between">
              {/* Menu toggle button - always visible */}
              <button
                type="button"
                className="btn btn-sm px-3 font-size-16 header-item"
                onClick={handleSidebarToggle}
              >
                <i className="fas fa-fw fa-bars"></i>
              </button>

              {/* Patient information from token */}
              <div className="d-flex">

              {/* Health System Name Display (patient-facing flow) */}
              {healthcareSystemInfo?.name && (
                  <a className="text-primary p-2 text-decoration-none text-center" href={`https://redirect.ankr.us/${healthcareSystemInfo.name.replace(/\s+/g, '').toLowerCase()}`} target="_blank" rel="noreferrer">
                    <i className="d-none d-sm-inline fas fa-hospital me-1"></i>
                    {healthcareSystemInfo.name}
                  </a>
              )}

              {/* Healthcare context switcher for admin/multitenant users */}
              {user && <HealthcareSelector className="px-2 d-none d-sm-flex" />}

              {/* Organization context switcher for sales users */}
              {user && <OrganizationSelector className="px-2 d-none d-sm-flex" />}

              {/* <div className="dropdown d-none d-sm-inline-block">
                              <button type="button" className="btn header-item"
                              data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                  <img id="header-lang-img" src="dist/assets/images/flags/us.jpg" alt="Header Language" width="24" height="auto"/>
                              </button>
                              <div className="dropdown-menu dropdown-menu-end">
                                  <button type="button" className="dropdown-item notify-item language" data-lang="en" onClick={() => console.log('Language English clicked')}>
                                      <img src="dist/assets/images/flags/us.jpg" alt="user-image" className="me-1" width="24" height="auto"/> <span className="align-middle">English</span>
                                  </button>
                                  <div className="dropdown-divider"></div>
                                  <button type="button" className="dropdown-item notify-item language" onClick={() => console.log('Coming soon clicked')}><strong>Coming soon</strong></button>
                                  <button type="button" className="dropdown-item notify-item language" data-lang="sp" onClick={() => console.log('Language Spanish clicked')}>
                                      <img src="dist/assets/images/flags/spain.jpg" alt="user-image" className="me-1" width="24" height="auto"/> <span className="align-middle">Spanish</span>
                                  </button>
                                  <button type="button" className="dropdown-item notify-item language" data-lang="gr" onClick={() => console.log('Language German clicked')}>
                                      <img src="dist/assets/images/flags/germany.jpg" alt="user-image" className="me-1" width="24" height="auto"/> <span className="align-middle">German</span>
                                  </button>
                                  <button type="button" className="dropdown-item notify-item language" data-lang="it" onClick={() => console.log('Language Italian clicked')}>
                                      <img src="dist/assets/images/flags/italy.jpg" alt="user-image" className="me-1" width="24" height="auto"/> <span className="align-middle">Italian</span>
                                  </button>
                                  <button type="button" className="dropdown-item notify-item language" data-lang="ru" onClick={() => console.log('Language Mandarin clicked')}>
                                      <img src="dist/assets/images/flags/china.jpg" alt="user-image" className="me-1" width="24" height="auto" /> <span className="align-middle">Mandarin</span>
                                  </button>
                              </div>
                          </div> */}

                          <div className="dropdown d-none d-sm-inline-block">
                              <button
                                type="button"
                                className="btn header-item"
                                id="mode-setting-btn"
                                onClick={handleDarkModeToggle}
                              >
                                  <i className="fas fa-moon icon-lg layout-mode-dark"></i>
                                  <i className="fas fa-sun icon-lg layout-mode-light"></i>
                              </button>
                          </div>

                <NotificationCenter />

                {/* <div className="dropdown d-inline-block">
                  <button type="button" className="btn header-item right-bar-toggle me-2">
                      <i className="fas fa-cog icon-lg"></i>
                  </button>
                </div> */}

                {/* Admin/Sales User Profile Dropdown */}
                {user && <UserProfileDropdown className="d-inline-block" />}

                {/* Patient Profile Dropdown - only show if no admin user logged in */}
                {!user && (patientInfo?.firstName || patientInfo?.lastName) ? (
                  <div className="dropdown d-inline-block ml-2">
                    <button
                      type="button"
                      className="btn header-item bg-light-subtle border-start border-end"
                      id="patient-info-dropdown"
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      aria-haspopup="true"
                      aria-expanded={dropdownOpen}
                    >
                    <div className="d-flex align-items-center">
                      {avatar.initials ? (
                        <div style={avatar.style}>
                          {avatar.initials}
                        </div>
                      ) : (
                        <img className="rounded-circle header-profile-user" src="dist/assets/images/users/avatar-1.jpg"
                          alt="Header Avatar"/>
                      )}
                      <div className="ms-2">
                        <span className="d-none d-sm-inline-block fw-medium">
                          {`${patientInfo?.firstName || ''} ${patientInfo?.lastName || ''}`.trim()}
                        </span>
                        <i className={`fa fa-chevron-${dropdownOpen ? 'up' : 'down'} d-none d-xl-inline-block ms-1`}></i>
                      </div>
                    </div>
                    </button>
                    <div className={`dropdown-menu dropdown-menu-end ${dropdownOpen ? 'show' : ''}`} style={{right:0,zIndex:9999}}>
                        <a className="dropdown-item" href="#"><i className="fas fa-hospital-user me-1"></i> <strong>Your ID</strong> {patientInfo?.id || 'N/A'}</a>
                        <a className="dropdown-item" href="#"><i className="fas fa-calendar-check me-1"></i> <strong>DOB</strong> {patientInfo?.dob || 'N/A'}</a>
                        <a className="dropdown-item" href="#"><i className="fas fa-envelope me-1"></i> <strong>Email</strong> {patientInfo?.email || 'N/A'}</a>
                        <a className="dropdown-item" href="#"><i className="fas fa-phone me-1"></i> <strong>Phone</strong> {patientInfo?.primaryNumber || 'N/A'}</a>
                      <div className="dropdown-divider"></div>
                      <button className="dropdown-item" onClick={handleLogout}><i className="fas fa-sign-out-alt me-1"></i> Logout</button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </header>

          {/* ========== Left Sidebar Start ========== */}
          <div className="vertical-menu">
            {/* LOGO - Fixed at top of sidebar */}
            <div className="navbar-brand-box mb-1">
              {/* Logo for dark theme */}
              <a href="/" className="logo logo-dark">
                <span className="logo-sm">
                  <img src="/dist/assets/images/ankr_health_logo.webp" alt="Ankr Logo" width="120px" height="auto" />
                </span>
                <span className="logo-lg">
                  <img src="/dist/assets/images/ankr_health_logo.webp" alt="Ankr Logo" width="120px" height="auto" />
                </span>
              </a>

              {/* Logo for light theme */}
              <a href="/" className="logo logo-light">
                <span className="logo-sm">
                  <img className="inverted-image" src="/dist/assets/images/ankr_health_logo.webp" alt="Ankr Logo" width="120px" height="auto" />
                </span>
                <span className="logo-lg">
                  <img className="inverted-image" src="/dist/assets/images/ankr_health_logo.webp" alt="Ankr Logo" width="120px" height="auto" />
                </span>
              </a>
            </div>

            <div data-simplebar="init" className="pt-4 h-100">
              <div className="simplebar-wrapper">
                <div className="simplebar-height-auto-observer-wrapper">
                  <div className="simplebar-height-auto-observer"></div>
                </div>
                <div className="simplebar-mask">
                  <div className="simplebar-offset">
                    <div className="simplebar-content-wrapper">
                      <div className="simplebar-content">
                        {/* Sidebar Navigation */}
                        <Navigation />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Left Sidebar End */}

          {/* ============================================================== */}
          {/* Start right Content here */}
          {/* ============================================================== */}
          <div className="main-content">
            <div className="page-content">
              <div className="container-fluid">
                <Routes>
                  {/* Default route redirects based on domain */}
                  <Route path="/" element={<RootRedirect />} />
                  {/* Patient routes */}
                  <Route path="/check-in" element={<CheckInFlow />} />
                  <Route path="/check-in-voice" element={<CheckInFlowWithVoice />} />
                  <Route path="/voice-demo" element={<VoiceIntakeDemo />} />
                  <Route path="/thank-you" element={<ThankYouPage />} />
                  <Route path="/support-desk" element={<SupportDeskPage />} />
                  <Route path="/vcx-onboarding" element={<VCXFSSOnboarding />} />

                  {/* Admin routes */}
                  <Route path="/admin/login" element={<LoginPage />} />
                  <Route path="/admin/permission-denied" element={
                    <AdminRoute>
                      <PermissionDeniedPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin" element={
                    <AdminRoute>
                      <AdminDashboardPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/profile" element={
                    <AdminRoute>
                      <UserProfilePage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/profile/preferences" element={
                    <AdminRoute>
                      <UserProfilePage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/initiate-visit" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <InitiateVisitPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/forms" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <FormsManagementPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/forms/new" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <FormsManagementPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/forms/edit/:formId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <FormsManagementPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/health-systems" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <HealthSystemsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/health-systems/edit/:healthSystemId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <HealthSystemsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/mfa-module" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <MfaModulePage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/credentials" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <CoreServerCredentialsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/health-systems/:healthSystemId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <HealthSystemsPage />
                    </AdminRoute>
                  } />
                  {/* Route for Callback Management */}
                  <Route path="/admin/callbacks" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <CallbackManagementPage />
                    </AdminRoute>
                  } />
                  {/* Route for Scope Management */}
                  <Route path="/admin/scopes" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <ScopeManagementPage />
                    </AdminRoute>
                  } />
                  {/* Route for Note Templates Management */}
                  <Route path="/admin/note-templates" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <NoteTemplatesPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/note-templates/create" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <NoteTemplateManagementPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/note-templates/edit/:templateId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <NoteTemplateManagementPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/note-templates/:templateId/associations" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <NoteTemplateAssociationsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/roles" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <RolesPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/roles/:roleId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <RolesPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/users/ankr" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <AnkrUsersPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/users/ankr/:userId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <AnkrUsersPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/users/ankr/:userId/:tab" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <AnkrUsersPage />
                    </AdminRoute>
                  } />

                  <Route path="/admin/users/healthcare" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <HealthcareUsersPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/users/healthcare/:userId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <HealthcareUsersPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/users/healthcare/:userId/:tab" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <HealthcareUsersPage />
                    </AdminRoute>
                  } />

                  <Route path="/admin/users/patients" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <PatientsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/users/patients/:patientId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <PatientsPage />
                    </AdminRoute>
                  } />

                  <Route path="/admin/users" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <UsersPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/users/create" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <UsersPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/users/edit/:userId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <UsersPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/users/:userId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <UsersPage />
                    </AdminRoute>
                  } />

                  <Route path="/admin/patients" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <PatientManagementPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/patients/:patientId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <PatientManagementPage />
                    </AdminRoute>
                  } />

                  <Route path="/admin/providers" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <ProviderManagementPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/providers/:providerId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <ProviderManagementPage />
                    </AdminRoute>
                  } />

                  <Route path="/admin/sales/users" element={
                    <AdminRoute>
                      <SalesUsersPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/sales/users/:userId" element={
                    <AdminRoute>
                      <SalesUsersPage />
                    </AdminRoute>
                  } />

                  <Route path="/admin/sales/roles" element={
                    <AdminRoute>
                      <SalesRolesPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/sales/roles/:roleId" element={
                    <AdminRoute>
                      <SalesRolesPage />
                    </AdminRoute>
                  } />

                  <Route path="/admin/sales/organizations" element={
                    <AdminRoute>
                      <OrganizationsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/sales/organizations/:orgId" element={
                    <AdminRoute>
                      <OrganizationsPage />
                    </AdminRoute>
                  } />

                  <Route path="/admin/sales/teams" element={
                    <AdminRoute>
                      <SalesTeamsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/sales/teams/:teamId" element={
                    <AdminRoute>
                      <SalesTeamsPage />
                    </AdminRoute>
                  } />

                  <Route path="/admin/sales/leads" element={
                    <AdminRoute>
                      <SalesLeadsPage />
                    </AdminRoute>
                  } />

                  <Route path="/admin/sales/opportunities" element={
                    <AdminRoute>
                      <SalesOpportunityPage />
                    </AdminRoute>
                  } />

                  <Route path="/admin/sales/job-listings" element={
                    <AdminRoute>
                      <JobListingsPage />
                    </AdminRoute>
                  } />

                  <Route path="/admin/sales/analytics" element={
                    <AdminRoute>
                      <RetellAnalyticsDashboard />
                    </AdminRoute>
                  } />

                  <Route path="/admin/sales/call-logs" element={
                    <AdminRoute>
                      <RetellCallLogs />
                    </AdminRoute>
                  } />

                  <Route path="/admin/voiceai/credentials" element={
                    <AdminRoute>
                      <VoiceAICredentialsManager />
                    </AdminRoute>
                  } />

                  <Route path="/admin/sales/settings" element={
                    <AdminRoute>
                      <SalesModuleSettings />
                    </AdminRoute>
                  } />

                  {/* Referral Management Routes */}
                  <Route path="/admin/referrals/settings" element={
                    <AdminRoute>
                      <ReferralManagementSettings />
                    </AdminRoute>
                  } />
                  <Route path="/admin/referrals" element={
                    <AdminRoute>
                      <ReferralManagementPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/referrals/:referralId" element={
                    <AdminRoute>
                      <ReferralManagementPage />
                    </AdminRoute>
                  } />

                  {/* Public ROI Calculator - no auth required */}
                  <Route path="/roi-calculator" element={<ROICalculator />} />

                  {/* VCX Events & Voice AI Agents - Auto-scoped routes without health_system_id */}
                  <Route path="/admin/vcx-events" element={
                    <AdminRoute>
                      <VCXEventsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/vcx-events/domain/:domain" element={
                    <AdminRoute>
                      <VCXEventsPage />
                    </AdminRoute>
                  } />
                  {/* Edit mode routes - must come before tab routes to avoid ambiguity */}
                  <Route path="/admin/vcx-events/domain/:domain/:eventId/edit/:tab" element={
                    <AdminRoute>
                      <VCXEventsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/vcx-events/domain/:domain/:eventId/edit" element={
                    <AdminRoute>
                      <VCXEventsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/vcx-events/:eventId/edit/:tab" element={
                    <AdminRoute>
                      <VCXEventsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/vcx-events/:eventId/edit" element={
                    <AdminRoute>
                      <VCXEventsPage />
                    </AdminRoute>
                  } />
                  {/* Routes with tab parameter - must come before routes without tab */}
                  <Route path="/admin/vcx-events/domain/:domain/:eventId/:tab" element={
                    <AdminRoute>
                      <VCXEventsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/vcx-events/:eventId/:tab" element={
                    <AdminRoute>
                      <VCXEventsPage />
                    </AdminRoute>
                  } />
                  {/* Routes without tab parameter */}
                  <Route path="/admin/vcx-events/domain/:domain/:eventId" element={
                    <AdminRoute>
                      <VCXEventsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/vcx-events/:eventId" element={
                    <AdminRoute>
                      <VCXEventsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/voiceai-agents" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <VoiceAIAgentsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/voiceai-agents/create" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <VoiceAIAgentsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/voiceai-agents/:agentId/edit" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <VoiceAIAgentsPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/voiceai-agents/:agentId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <VoiceAIAgentsPage />
                    </AdminRoute>
                  } />


                  <Route path="/admin/cases" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <CasesPage />
                    </AdminRoute>
                  } />
                  
                  {/* More specific routes must come before less specific ones */}
                  <Route path="/admin/cases/:caseId/audit-logs" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <CasesPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/cases/:caseId/notes" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <CasesPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/cases/:caseId/link-management" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <CasesPage />
                    </AdminRoute>
                  } />

                  {/* Routes for Patient Details */}
                  <Route path="/admin/patients/edit/:patientId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <PatientDetailsPage />
                    </AdminRoute>
                  } />
                  {/* Route for Novu Subscriber Management */}
                  <Route path="/admin/novu-subscribers" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <NovuSubscriberManagerPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/short-url-management/:caseId" element={
                    <AdminRoute>
                      <ShortUrlManagementPage />
                    </AdminRoute>
                  } />
                  
                  <Route path="/zoom-meeting" element={<ZoomMeetingPage />} />
                  {/* Catch-all for unmatched /admin/* paths */}
                  <Route path="/admin/*" element={
                    <AdminRoute>
                      <AdminNotFoundPage />
                    </AdminRoute>
                  } />
                  {/* Route to handle short URLs and other paths */}
                  <Route path="/*" element={<CheckInFlow />} />
                  <Route path="/admin/forms/preview/:formId" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <FormPreviewPage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/mfa-module" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <MfaModulePage />
                    </AdminRoute>
                  } />
                  <Route path="/admin/note-generation-sandbox" element={
                    <AdminRoute requireAnkrAdmin={true}>
                      <NoteGenerationSandbox />
                    </AdminRoute>
                  } />
                </Routes>
              </div>
            </div>
          </div>
          {/* end main content*/}

          {/* Global Feedback Side Panel - Always available */}
          <FeedbackSidePanel
            onSubmit={handleSessionFeedbackSubmit}
            sessionData={FeedbackTriggerService.getSessionDataForFeedback()}
          />

    </div>
  );
}

export default App;